import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
  Tooltip,
  TableSortLabel,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import {
  GetUserRoutes,
  MarkCompletedUserRoute,
} from "../../../services/UserRoutesService";
import ViewUserRoute from "./ViewUserRoute";
import UserRouteMap from "./UserRouteMap";
import ClientCoCPrintPopup from "./ClientCoCPrintPopup";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import CommonLoader from "../../common/CommonLoader";
import AddIcon from "@mui/icons-material/Add";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import {
  getComparator,
  stableSort,
} from "../../../../common-utilities/sortingUtils";

const UserRoutesTable = () => {
  const columns = [
    { id: "id", label: "Route Id" },
    { id: "routeName", label: "Route Name" },
    { id: "userId", label: "User" },
    { id: "routeDate", label: "Date" },
    { id: "routeClients", label: "Clients" },
    { id: "actions", label: "Actions" },
  ];

  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [routesData, setRoutesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        setLoading(true);
        const response = await GetUserRoutes();
        setRoutesData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching routes:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRoutes();
  }, [isDeleted]);

  useEffect(() => {
    if (!routesData) return;
    const filteredResults = routesData.filter((route) =>
      columns.some((col) => {
        const value = route[col.id];
        return value && typeof value === "string"
          ? value.toLowerCase().includes(searchTerm.toLowerCase())
          : false;
      })
    );
    setFilteredData(filteredResults);
  }, [searchTerm, routesData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleViewClick = (route) => {
    setSelectedRoute(route);
  };

  const handleBackClick = () => {
    setSelectedRoute(null);
    setSearchTerm("");
  };



  const handleAddRouteClick = () => {
    navigate("/vlp/user-routes/add");
  };


  const handlePrintClick = (route) => {
    setSelectedRoute(route);
    setIsPrintDialogOpen(true);
  };

  const handlePrintDialogClose = () => {
    setIsPrintDialogOpen(false);
    setSelectedRoute(null);
  };

  const setRouteClients = () => {};
  const printRouteClientCoC = (route) => {
    handlePrintClick(route);
  };

  const markCompletedRoute = async (route) => {
    try {
      setLoading(true);
      const response = await MarkCompletedUserRoute(route.id);
      if (response.results === true) {
        NotificationManager.success("Route completed successfully");
        setIsDeleted(!isDeleted);
      } else {
        NotificationManager.error("Error completing route");
      }
    } catch (error) {
      NotificationManager.error("Error completing route");
    } finally {
      setLoading(false);
    }
  };

  const renderActions = (route) => (
    <div style={{ minWidth: "120px" }}>
      <Tooltip title="Print Chain of Custody">
        <IconButton
          aria-label="done"
          style={{ color: "#0eed25" }}
          onClick={() => printRouteClientCoC(route)}
        >
          <LocalPrintshopIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Mark as Completed">
        <IconButton
          aria-label="done"
          style={{ color: "#c603fc" }}
          onClick={() => markCompletedRoute(route)}
        >
          <DoneAllIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="View Route">
        <IconButton
          aria-label="view"
          style={{ color: "green" }}
          onClick={() => handleViewClick(route)}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
    </div>
  );

  return (
    <>
      <span className="title">User Routes</span>
      <hr />
      {selectedRoute && !isPrintDialogOpen ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewUserRoute route={selectedRoute} />
          <UserRouteMap
            accounts={selectedRoute.routeClients}
            onAccountsOrderChange={setRouteClients}
            isForView={true}
          />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddRouteClick}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.id}>
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : "asc"}
                          onClick={() => handleRequestSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stableSort(filteredData, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((route, index) => (
                      <TableRow
                        key={route.id}
                        sx={{
                          backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#fff",
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        {columns.slice(0, -1).map((col) => (
                          <TableCell key={col.id}>
                            {col.id === "routeDate" && route[col.id]
                              ? new Date(route[col.id]).toLocaleDateString(
                                  "en-US"
                                )
                              : route[col.id]
                              ? String(route[col.id]).substring(0, 35)
                              : ""}
                          </TableCell>
                        ))}
                        <TableCell>{renderActions(route)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </>
      )}
      
      {isPrintDialogOpen && (
        <ClientCoCPrintPopup
          open={isPrintDialogOpen}
          routeClients={selectedRoute.routeClients}
          onClose={handlePrintDialogClose}
        />
      )}

      <CommonLoader loading={loading} />
    </>
  );
};

export default UserRoutesTable;
